import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
  useController,
  UseControllerProps,
  FieldValues,
  FieldError,
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useDebounce } from 'use-debounce';
import { useGetEtisStops } from '@/api/trafikportalen';
import { Autocomplete } from '@/components/Autocomplete';
import { cn } from '@/helpers/classnames';
import { useRecentStops } from '@/hooks/useRecentStops';
import { IconSearch } from '@/icons';
import type { EtisStopDTO } from '@/api/trafikportalen';

interface InputBusStopProps<
  T extends FieldValues,
> extends UseControllerProps<T> {
  editMode?: boolean;
  noValueDescription?: ReactNode;
  error?: FieldError;
  helperText?: string;
  helperTextReserveSpace?: boolean;
  className?: string;
}

export const InputBusStop = <T extends FieldValues>({
  editMode,
  noValueDescription,
  error,
  helperText,
  helperTextReserveSpace = false,
  className,
  ...props
}: InputBusStopProps<T>) => {
  const { field } = useController(props);
  const { formatMessage } = useIntl();
  const { recentStops, addRecentStop } = useRecentStops();

  const value: string = field.value ?? '';
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 300);
  const { data: etisStops } = useGetEtisStops(
    { searchString: debouncedSearch },
    { query: { enabled: editMode === true && debouncedSearch.length >= 2 } },
  );

  const [storedStops, setStoredStops] = useState<EtisStopDTO[]>([]);

  useEffect(() => {
    if (etisStops) {
      setStoredStops(etisStops);
    }
  }, [etisStops]);

  const stopOptions = useMemo(() => {
    const names = [
      ...(value ? [value] : []),
      ...recentStops.map((stop) => stop.name),
      ...storedStops.map((stop) => stop.name),
    ];
    return [...new Set(names)].map((name) => ({ label: name, value: name }));
  }, [value, recentStops, storedStops]);

  const handleValueChange = useCallback(
    (newValue: string) => {
      setSearch('');
      field.onChange(newValue);

      const selectedStop = storedStops.find((stop) => stop.name === newValue);
      if (selectedStop) {
        addRecentStop(selectedStop);
      }
    },
    [field, storedStops, addRecentStop],
  );

  const handleSearchChange = useCallback(
    (newSearch: string) => {
      setSearch(newSearch);
      if (!newSearch) {
        field.onChange('');
      }
    },
    [field],
  );

  return editMode === true ? (
    <Autocomplete
      id={field.name}
      options={stopOptions}
      value={value}
      onValueChange={handleValueChange}
      onSearchChange={handleSearchChange}
      placeholder={formatMessage({ defaultMessage: 'Sök hållplats' })}
      startAdornment={<IconSearch />}
      error={error?.message}
      helperText={helperText}
      helperTextReserveSpace={helperTextReserveSpace}
      className={className}
    />
  ) : (
    <span
      className={cn('break-all', !value && 'italic text-text-700', className)}
    >
      {(value || noValueDescription) ??
        formatMessage({ defaultMessage: 'Inget värde' })}
    </span>
  );
};
